import { ActionContext, ActionTree } from 'vuex'
import { cloneDeep } from 'lodash'
import { IProductsResponse, IProductsState, IRootState } from '@/@types'
import { fetchPassTypes, getAvailableProducts } from '@/models/products'
import { actionErrorHandling } from '@/helpers'
import CONSTANTS from '@/constants'

const actions: ActionTree<IProductsState, IRootState> = {
  fetchAvailableProducts: async (context: ActionContext<IProductsState, IRootState>) => {
    try {
      const {
        PLANS,
        PLAN_STATES,
        VOUCHERS,
        LIVE_VIRTUAL_TRAININGS,
        BUSINESS_PLANS,
        SKILL_DIVE_LEARNING_AREAS,
      } = CONSTANTS
      const {
        BAH,
        BLKFRIDAY,
        EDA_PREMIUM,
        EDA_PREMIUM_CONT,
        INE_PREMIUM_BOOGO_2Y,
        LEGACY,
        MONTHLY,
        MONTHLY_EJPT,
        PREMIUM,
        PREMIUM2YR,
        PREMIUM_MONTHLY,
        PTA,
        QUARTERLY_EJPT,
        EWPT_PREMIUM,
        SKILL_DIVE_ANNUAL,
      } = PLANS
      const fallback = Object.values(PLANS)
      const requestedPlans = new URLSearchParams(window.location.search).get('available_plans')
      const matches =
        requestedPlans && requestedPlans.length
          ? requestedPlans
              .split(',')
              .map(name => name.trim())
              .filter(name => fallback.includes(name))
          : []
      const cleanNames = matches && matches.length ? matches : fallback
      const coupon = context.rootState.shoppingCart?.coupon
      const products = await getAvailableProducts(coupon)
      const {
        subscriptions,
        certification_vouchers,
        bootcamps,
        current_subscriptions,
        live_virtual_trainings,
        skill_dive,
      }: IProductsResponse = products
      let subscriptionFiltered = subscriptions
      if (requestedPlans?.includes(BAH)) {
        context.commit('ONE_EXCLUSIVE_PLAN_UPDATED', true)
        subscriptionFiltered = subscriptionFiltered.filter(plan => plan.merchant_id === BAH)
      }
      if (skill_dive) {
        const { NETWORKING, CYBER_SECURITY, CLOUD } = SKILL_DIVE_LEARNING_AREAS
        skill_dive.forEach(addOn => {
          switch (addOn.merchant_id!) {
            case NETWORKING:
              addOn.label = 'Networking'
              break
            case CYBER_SECURITY:
              addOn.label = 'Cyber Security'
              break
            case CLOUD:
              addOn.label = 'Cloud'
              break
          }
        })
      }
      const availablePlans = Object.values(subscriptionFiltered)
        .map(current => {
          const cloneCurrent = cloneDeep(current)
          // plan term prop hydratation
          switch (current.merchant_id!) {
            case MONTHLY:
            case PREMIUM_MONTHLY:
            case MONTHLY_EJPT:
              current.term = 'month'
              break
            case PREMIUM2YR:
              current.term = '2 year'
              break
            case QUARTERLY_EJPT:
              current.term = 'quarterly'
              break
            default:
              current.term = 'year'
              break
          }
          // plan details hydratation
          switch (current.merchant_id) {
            case MONTHLY_EJPT:
            case QUARTERLY_EJPT:
              current.badge = 'ejpt_badge'
              current.nameToShow = 'Junior Penetration Tester Exam Voucher'
              current.nameShort = 'eJPT'
              current.promoInfo = {
                title: '+ 3 months of Fundamentals*',
                mobile: '+3 mo Fundamentals Quarterly FREE*',
                desktop: '+3 months of Fundamentals Quarterly FREE*',
              }
              current.promoInfoExtra = '*After 3 months, $117 billed quarterly.'
              current.alreadyINE = 'an INE'
              current.bestValue = '+3 months of Fundamentals Quarterly'
              current.bestValue = '+3 months of Fundamentals Quarterly'
              current.bundleShortInfo = 'eJPT + 3 months of Fundamentals'
              break
            case EDA_PREMIUM:
              current.badge = 'eEDA_badge'
              current.nameToShow = 'Enterprise Defense Associate Exam Voucher'
              current.nameShort = 'eEDA'
              current.promoInfo = {
                title: '+ 3 months of Premium*',
                mobile: '+ 3 mo of Premium FREE*',
                desktop: '+ 3 months of Premium FREE*',
              }
              current.promoInfoExtra =
                '*Renewal: $350 after 3 months, then $749 annually from purchase date.'
              current.alreadyINE = 'a Premium'
              current.bestValue = '+3 months of Premium'
              current.bundleShortInfo = 'eEDA+ 3 months of Premium'
              break
            case EWPT_PREMIUM:
              current.badge = 'eWPT_badge'
              current.nameToShow = ''
              current.nameShort = 'eWPT'
              current.promoInfo = {
                title: '+ 3 months of Premium*',
                mobile: '+ 3 mo of Premium FREE*',
                desktop: '+ 3 months of Premium FREE*',
              }
              current.promoInfoExtra =
                '*Renewal: $350 after 3 months, then $749 annually from purchase date.'
              current.alreadyINE = 'a Premium'
              current.bestValue = ''
              current.bundleShortInfo = 'eWPT+ 3 months of Premium'
              break
            case SKILL_DIVE_ANNUAL:
              current.priceToShow = cloneCurrent.price_cents
              current.totalHours = 240
              current.addOns = skill_dive
              current.addOnsOnCart = []
              break
            default:
              break
          }
          return current
        })
        .filter(plan => plan.merchant_id && cleanNames.includes(plan.merchant_id))
      const userSubscriptions = Object.values(current_subscriptions)
        .map(sub => {
          if (sub.plan_code === LEGACY) {
            return
          }
          return sub
        })
        .filter(
          userSub =>
            userSub?.plan_code &&
            [PLAN_STATES.active, PLAN_STATES.pending, PLAN_STATES.cancelled].includes(userSub.state)
        )
      const userHasSkillDive = userSubscriptions.find(
        sub => sub?.plan_code === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL
      )

      certification_vouchers.forEach(voucher => {
        switch (voucher.merchant_id) {
          case VOUCHERS.ICCA:
            voucher.version = 'v1.0'
            voucher.nameLong = 'INE Certified Cloud Associate Exam Voucher'
            voucher.descriptionOld = `The INE Certified Cloud Associate (ICCA) exam is an entry-level certification 
              designed for anyone interested in becoming part of the Cloud conversation within their organization. 
              This certification covers Cloud Concepts, Cloud Services, and Cloud Security and 
              Regulatory Compliance through real-world, multiple-choice questions and hands-on lab challenges. 
              The ICCA covers general cloud concepts and how they are implemented in three of the largest 
              cloud provider platforms, including AWS, Azure, and GCP Cloud Platform. 
              If you want to begin your journey in cloud, increase your knowledge of multiple cloud platforms, 
              or become part of the cloud conversation, this certification is for you.`
            voucher.price = `$${voucher.price_cents! / 100}`
            break
          case VOUCHERS.EJPT:
            voucher.nameLong = 'Junior Penetration Tester Exam Voucher'
            voucher.descriptionOld = `The Jr. Penetration Tester exam (eJPT) validates that the individual 
              has the knowledge and skills required to fulfill a role as an entry-level penetration tester. 
              This certification covers Assessment Methodologies and Enterprise Auditing with Host, 
              Network, and Web Application Penetration Testing. This exam is designed to be the first milestone certification 
              for someone with little to no experience in cybersecurity, simulating the skills utilized during a 
              real-world engagement. This exam truly shows yourself and your employer that 
              you have what it takes to be part of a winning penetration testing team.`
            voucher.price = `$${voucher.price_cents! / 100}`
            break
          case VOUCHERS.EDA:
            voucher.nameLong = 'Enterprise Defense Associate Exam Voucher'
            voucher.descriptionOld = `The Enterprise Defense Administrator (eEDA) exam is designed for professionals 
              that are just starting their defensive cybersecurity or security engineering journey. 
              This certification covers Secure Engineering Fundamentals, Governance Risk and Compliance, Identity and 
              Access Management, and Security Administration. This exam is designed to give you the skills you'll need to 
              succeed as a Defensive Security Engineer, all while testing your abilities in a hands-on testing environment. 
              While this certification is the perfect first milestone certification for someone with little to no experience 
              in cybersecurity, a background in IT is highly recommended.`
            voucher.price = `$${voucher.price_cents! / 100}`
            break
          default:
            break
        }
      })

      if (live_virtual_trainings) {
        live_virtual_trainings?.forEach(liveVirtualTraining => {
          switch (liveVirtualTraining.merchant_id) {
            case LIVE_VIRTUAL_TRAININGS.CCIE_SECURITY_V6:
              liveVirtualTraining.description = `This instructor-led training is designed for network security professionals 
                who want to achieve the CCIE Security certification. This course provides students with the knowledge and 
                skills needed to design, deploy, configure, manage, and troubleshoot Cisco network security solutions. 
                The CCIE Security instructor-led training program provides students with advanced knowledge and skills 
                in securing complex networks. This program covers the essential topics of network security, 
                including advanced security technologies, network design, implementation, and troubleshooting. 
                By the end of the program, students will be well-versed in the field of network security 
                and will have the necessary skills to be ready for attempting the CCIE Security lab exam.`
              liveVirtualTraining.instructorName = 'Rohit Pardasani'
              liveVirtualTraining.instructorAvatar =
                'https://s3.amazonaws.com/api.ine.com/production/instructors/2019-06-26-00-20-18-lxfanDWbJZ1o20sKfPEoiZdJInXPWUc9WS2xKL2w.jpeg'
              liveVirtualTraining.durationReadable =
                'Monday, September 11th - Friday, September 15th'
              liveVirtualTraining.trainingDayOneReadable = 'September 11th'
              liveVirtualTraining.planDiscount = [
                BAH,
                BLKFRIDAY,
                PREMIUM2YR,
                PREMIUM,
                PREMIUM_MONTHLY,
                EDA_PREMIUM,
                EWPT_PREMIUM,
                PTA,
                INE_PREMIUM_BOOGO_2Y,
                EDA_PREMIUM_CONT,
                BUSINESS_PLANS.PLAN_ENTERPRISE,
                BUSINESS_PLANS.PLAN_PROFESSIONAL,
                BUSINESS_PLANS.PLAN_ENTERPRISE_LAB,
              ]
              liveVirtualTraining.originalPriceCents = liveVirtualTraining.price_cents
              break
            case LIVE_VIRTUAL_TRAININGS.LVT_CHATGPT_AND_IA:
              liveVirtualTraining.description =
                'This live training session, led by industry recognized expert Tim Warner, will introduce you to what ChatGPT is and how to use it. Not only that, but Tim will go further to help you understand generative AI - the foundational models that ChatGPT is built on, and demonstrate tools that work on the same principles as ChatGPT. You will get live demonstrations of the techniques that will allow you to use generative AI tools like ChatGPT to create a variety of work products. You will also learn how to use generative AI to create code with GitHub Copilot and how to implement generative AI to gain insights on your own data with Azure Open AI. Don’t worry, no previous coding experience or data analytics experience is necessary.'
              liveVirtualTraining.instructorName = 'Tim Warner'
              liveVirtualTraining.instructorAvatar =
                'https://assets.ine.com/instructors/ine-tim-warner-final.jpg'
              liveVirtualTraining.durationReadable = 'October 23-27, 11am-1pm ET'
              liveVirtualTraining.trainingDayOneReadable = 'October 23rd'
              liveVirtualTraining.planDiscount = [
                BAH,
                BLKFRIDAY,
                PREMIUM2YR,
                PREMIUM,
                PREMIUM_MONTHLY,
                EDA_PREMIUM,
                EWPT_PREMIUM,
                PTA,
                INE_PREMIUM_BOOGO_2Y,
                EDA_PREMIUM_CONT,
                BUSINESS_PLANS.PLAN_ENTERPRISE,
                BUSINESS_PLANS.PLAN_PROFESSIONAL,
                BUSINESS_PLANS.PLAN_ENTERPRISE_LAB,
              ]
              liveVirtualTraining.originalPriceCents = liveVirtualTraining.price_cents
              break
            default:
              break
          }
        })
      }

      context.commit('AVAILABLE_PLANS_UPDATED', availablePlans)
      context.commit('AVAILABLE_CERTIFICATIONS_UPDATED', certification_vouchers || [])
      context.commit('AVAILABLE_LIVE_VIRTUAL_TRAININGS_UPDATED', live_virtual_trainings || [])
      context.commit('AVAILABLE_BOOTCAMPS_UPDATED', bootcamps || [])
      context.commit('subscriptions/USERS_CURRENT_SUBSCRIPTIONS_UPDATED', userSubscriptions || [], {
        root: true,
      })
      context.commit('subscriptions/USER_HAS_SKILL_DIVE_UPDATED', !!userHasSkillDive, {
        root: true,
      })
    } catch (error) {
      actionErrorHandling(context, error)
      throw error
    }
  },
  getPassTypes: async (context: ActionContext<IProductsState, IRootState>) => {
    try {
      const passes = await fetchPassTypes()
      context.commit('PASS_TYPES_UPDATED', passes)
    } catch (error) {
      actionErrorHandling(context, error)
    }
  },
}

export default actions
